import {Injectable} from '@angular/core';
import {Event} from '@api/models/event/event';
import {INewsPost} from '@api/models/news/news';
import {PickerItem} from '@lib-modules/picker/components/picker-item/picker-item.component';
import {ISpecial} from '@api/models/specials/specials';
import {SpecialsHttpService} from '@api/http/specials/specials-http.service';
import {SportType} from '@lib/models/sport-type/sport-type';
import {PickerItemService} from '@lib-modules/picker/services/picker-item.service';
import {NewsCardService, NewsContentType} from '@lib-modules/cards/services/news-card/news-card.service';
import {VideoCardService, VideoContentType} from '@lib-modules/cards/services/video-card/video-card.service';
import {EventCardService, EventContentType} from '@lib-modules/cards/services/event-card/event-card.service';

@Injectable({
  providedIn: 'root'
})
export class HomePageService {

  public selectedState: PickerItem = undefined;
  public topNews: INewsPost[] = [];
  public studioNews: INewsPost[] = [];
  public otherNews: INewsPost[] = [];
  public topEvents: Event[] = [];
  public pastEvents: Event[] = [];
  public livestreams: Event[] = [];
  public replays: Event[] = [];
  public handballMatches: Event[] = [];
  public specials: ISpecial[] = [];
  public stateItems: PickerItem[] = [];
  public favoritesItems: PickerItem[] = [];
  public nationalLeagueItems: PickerItem[] = [];
  public internationalLeagueItems: PickerItem[] = [];
  public stateLeagueItems: PickerItem[] = [];
  public stateTeamItems: PickerItem[] = [];

  constructor(
    private eventCardService: EventCardService,
    private newsCardService: NewsCardService,
    private videoCardService: VideoCardService,
    private pickerItemService: PickerItemService,
    private specialsHttp: SpecialsHttpService,
  ) {
  }

  // MAIN CONTENT, always loads
  public async loadMainContent(): Promise<void> {
    this.topEvents = await this.eventCardService.fetchData(EventContentType.EventsByDate, this.selectedState, 0, 16, new Date());
    await this.loadMainNewsContent();
    await this.loadMainVideoContent();
  }

  public async loadStateItems() {
    this.stateItems = await this.pickerItemService.getStateItems();
  }

  private async loadMainNewsContent() {
    // Top News
    let news: INewsPost[];
    if (this.selectedState) {
      news = await this.newsCardService.fetchData(NewsContentType.EntityNews, this.selectedState, 0, 16, {editorial: true});
    } else {
      news = await this.newsCardService.fetchData(NewsContentType.TopNews, null, 0, 16);
    }
    this.topNews = news.slice(0, 4);
    this.otherNews = news.slice(4);
    // Studio News
    this.studioNews = await this.newsCardService.fetchData(NewsContentType.VideoNews, this.selectedState, 0, 12, { studio: true });
  }

  private async loadMainVideoContent() {
    this.livestreams = await this.videoCardService.fetchData(VideoContentType.ActiveStreams, this.selectedState);
    this.replays = await this.videoCardService.fetchData(VideoContentType.Replay, this.selectedState);
  }

  // PAGINATED CONTENT, loads on scrolling
  public async loadHandballVideos(): Promise<void> {
    const activeHandball = await this.videoCardService.fetchData(VideoContentType.ActiveStreams, null, SportType.Handball, 0, 8);
    const replayHandball = await this.videoCardService.fetchData(VideoContentType.Replay, null, SportType.Handball, 0, 8);
    this.handballMatches = [...activeHandball, ...replayHandball];
  }

  public async loadSpecials(): Promise<void> {
    if (this.specials.length > 0) {
      return;
    }
    const response = await this.specialsHttp.getSpecials();
    if (response?.data) {
      this.specials = response.data;
    }
  }

  public async loadPastEvents(): Promise<void> {
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    this.pastEvents = await this.eventCardService.fetchData(EventContentType.EventsByDate, this.selectedState, 0, 16, yesterday);
  }

  public async loadFavourites(): Promise<void> {
    this.favoritesItems = await this.pickerItemService.getFavoriteItems();
  }

  public async loadNationalLeagues(): Promise<void> {
    this.nationalLeagueItems = await this.pickerItemService.getNationalLeagueItems();
  }

  public async loadInternationalLeagues(): Promise<void> {
    this.internationalLeagueItems = await this.pickerItemService.getInternationalLeagueItems();
  }

  public async loadStateLeagueItems(): Promise<void> {
    if (!this.selectedState?.objectId) {
      return;
    }
    this.stateLeagueItems = await this.pickerItemService.getTopLeaguesForState(this.selectedState.objectId);
  }

  public async loadStateTeamItems(): Promise<void> {
    if (!this.selectedState?.objectId) {
      return;
    }
    this.stateTeamItems = await this.pickerItemService.getFootballTeamItems({stateId: this.selectedState.objectId});
  }
}
